// patientStore.js — zentrale Datenschicht für Patienten.
//
// LIVE-Modus:  liest/schreibt Supabase (Tabelle public.patients, RLS-gesichert)
// DEMO-Modus:  localStorage wie bisher
//
// In beiden Modi wird die Liste zusätzlich in localStorage gespiegelt
// ("dida_patients"), weil TermineScreen (Autocomplete) und
// TrackingScreen (Scorecard) diesen Cache lesen — die migrieren in
// späteren Slices auf eigene Store-Aufrufe.
import { supabase } from "./supabase";
import { demoFallbacks, serverActive } from "./mode";
import { INITIAL_PATIENTS } from "../data/patients";

const CACHE_KEY = "dida_patients";

// Spalten, die es in der DB-Tabelle gibt — alles andere (z.B. das alte
// "name"-Feld aus Demo-Daten) wird vor dem Insert/Update entfernt.
const DB_COLS = ["vorname","nachname","dob","kv","treat","last","since","status","allergy","meds","prop","pzr","next","src","history","email"];
const pick = (obj) => Object.fromEntries(Object.entries(obj).filter(([k]) => DB_COLS.includes(k)));

export const loadPatientsCache = () => {
  try { const s = localStorage.getItem(CACHE_KEY); if (s) return JSON.parse(s); } catch {}
  // LIVE: leer starten (Clean Slate) — Demo-Patienten nur im DEMO-Modus
  return demoFallbacks() ? INITIAL_PATIENTS : [];
};
const saveCache = (pats) => { try { localStorage.setItem(CACHE_KEY, JSON.stringify(pats)); } catch {} };

export async function listPatients() {
  if (!serverActive()) return loadPatientsCache();
  const { data, error } = await supabase.from("patients").select("*").order("nachname").order("vorname");
  if (error) throw error;
  saveCache(data);
  return data;
}

export async function addPatient(p) {
  if (!serverActive()) {
    const pats = [...loadPatientsCache(), { ...p, id: Date.now() }];
    saveCache(pats);
    return pats;
  }
  const { error } = await supabase.from("patients").insert(pick(p));
  if (error) throw error;
  return listPatients();
}

// CSV-Import: Patienten upserten. Match über normalisierten Namen + Geburtsdatum —
// vorhandene Patienten werden aktualisiert (nur nicht-leere CSV-Werte), neue angelegt.
const matchKey = (p) => `${(p.vorname||"").trim().toLowerCase()}|${(p.nachname||"").trim().toLowerCase()}|${(p.dob||"").trim()}`;

export async function importPatients(rows) {
  const clean = rows.filter(r => (r.vorname||"").trim() && (r.nachname||"").trim());
  const existing = await listPatients();
  const byKey = new Map(existing.map(p => [matchKey(p), p]));
  const toAdd = [], toUpdate = [];
  clean.forEach(r => {
    const hit = byKey.get(matchKey(r));
    if (hit) {
      const patch = {};
      Object.entries(pick(r)).forEach(([k, v]) => {
        if (v !== "" && v !== undefined && v !== null && v !== hit[k]) patch[k] = v;
      });
      if (Object.keys(patch).length) toUpdate.push({ id: hit.id, patch });
    } else {
      toAdd.push({ status:"Aktiv", kv:"", treat:"", last:"—", since:"", allergy:"—", meds:"—", prop:false, pzr:"—", next:"—", src:"PVS-Import", history:[], email:"", ...pick(r) });
    }
  });

  if (!serverActive()) {
    let pats = loadPatientsCache();
    toAdd.forEach(p => { pats = [...pats, { ...p, id: Date.now() + Math.random() }]; });
    toUpdate.forEach(({ id, patch }) => { pats = pats.map(x => x.id === id ? { ...x, ...patch } : x); });
    saveCache(pats);
    return { added: toAdd.length, updated: toUpdate.length, patients: pats };
  }

  if (toAdd.length) {
    const { error } = await supabase.from("patients").insert(toAdd.map(pick));
    if (error) throw error;
  }
  for (const { id, patch } of toUpdate) {
    const { error } = await supabase.from("patients").update(patch).eq("id", id);
    if (error) throw error;
  }
  const patients = await listPatients();
  return { added: toAdd.length, updated: toUpdate.length, patients };
}

export async function updatePatient(id, patch) {
  if (!serverActive()) {
    const pats = loadPatientsCache().map(x => x.id === id ? { ...x, ...patch } : x);
    saveCache(pats);
    return pats;
  }
  const { error } = await supabase.from("patients").update(pick(patch)).eq("id", id);
  if (error) throw error;
  return listPatients();
}
